import $ from 'jquery';
import {loadStripeSDK} from '@wc-stripe/stripe-sdk';

/**
 * Base class for BNPL payment method messaging elements.
 *
 * Subclasses (ProductMessage, CartMessage, etc.) implement context-specific
 * logic for retrieving totals, currency, and DOM containers.
 *
 * @since 4.0.0
 */
class AbstractMessage {

    actions;

    stripe = null;

    elements = null;

    messageElement = null;

    elementOptions = {};

    countryCode = '';

    elementType = 'paymentMethodMessaging';

    _options = {};

    /**
     * Array of {id, paymentMethodType} objects for each supported BNPL gateway.
     *
     * @type {Array<{id: string, paymentMethodType: string}>}
     */
    paymentMethods = [];

    selector;

    location;

    /**
     * Supported currencies for BNPL messaging. If set, the element will only
     * be created when the current currency is in this list.
     *
     * @type {string[]}
     */
    currencies = [];

    countries = [];

    /**
     * @param {Object}   props
     * @param {Array}    props.paymentMethods - Array of {id, paymentMethodType} objects
     * @param {string}   props.selector - CSS selector for the container element
     * @param {string}   props.location - Placement hint for fallback DOM creation (e.g., 'above_price', 'below_total')
     * @param {string[]} props.currencies - Supported currency codes (e.g., ['USD', 'EUR'])
     */
    constructor(
        {
            actions,
            location,
            settings,
        }
    ) {
        this.actions = actions;
        this.location = location;
        this.selector = settings.selector;
        this.elementOptions = settings.elementOptions;
        this.countryCode = settings.countryCode;
        this.paymentMethods = settings.paymentMethods || [];
        this.currencies = settings.currencies || [];
        this.countries = settings.countries || [];
    }

    initialize() {
        this.loadStripeSDK().then(() => {
            this.createMessageElement();
            this.mountElement();
            this.initActions();
        }).catch(error => {
        });
    }

    initActions() {

    }

    async loadStripeSDK() {
        try {
            this.stripe = await loadStripeSDK();
            this.elements = this.stripe.elements(this.elementOptions);
        } catch (error) {
            console.log(`Error loading Stripe SDK. ${error}`);
            throw error;
        }
    }

    onReady(event) {
        if (this.getTotalCents() > 0) {
            this.showMessageElement();
        }
    }

    showMessageElement() {
        throw new Error('Method showMessageElement not implemented.');
    }

    /**
     * Returns true if the current currency and country are supported for BNPL messaging.
     *
     * @returns {boolean}
     */
    isSupported() {
        if (this.currencies.length && !this.currencies.includes(this.getCurrency().toUpperCase())) {
            return false;
        }
        if (this.countries.length && !this.countries.includes(this.getCountryCode())) {
            return false;
        }
        return true;
    }

    /**
     * Creates the Stripe paymentMethodMessaging element.
     * Skips creation if the current currency is not supported to prevent
     * Stripe JS exceptions.
     */
    createMessageElement() {
        if (!this.isSupported()) {
            return;
        }
        this.messageElement = this.elements.create(this.elementType, this.buildMessageElementOptions());
        this.messageElement.on('ready', this.onReady.bind(this));
    }

    /**
     * Determines whether the message element should be mounted.
     * Override in subclasses to add context-specific checks
     * (e.g., currency support, amount limits).
     *
     * @returns {boolean}
     */
    shouldMountElement(container) {
        return !$(container).children().length > 0;
    }

    /**
     * Mounts the message element to the DOM container returned by getElementContainer().
     * Called by createMessageElement() after the element is created.
     */
    mountElement() {
        if (!this.messageElement) {
            return;
        }
        const container = this.getElementContainer();
        if (!container) {
            return;
        }
        if (!this.shouldMountElement(container)) {
            return;
        }
        this.messageElement.mount(container);
    }

    unmountElement() {
        if (this.messageElement) {
            this.messageElement.unmount();
        }
    }

    updateElement() {
        if (this.messageElement) {
            this.messageElement.update(this.buildMessageElementOptions());
        }
    }

    hasPaymentMethodTypes() {
        return this._options?.paymentMethodTypes?.length > 0 || false;
    }

    getElementContainer() {
        const container = document.querySelector(this.selector);
        return container;
    }

    /**
     * Returns the total amount as a decimal string.
     * Subclasses must implement based on their data source (product, cart, etc.).
     *
     * @returns {string}
     */
    getTotal() {
        throw new Error('Subclasses must implement getTotal()');
    }

    /**
     * Returns the total amount in the smallest currency unit (e.g., cents).
     * Used as the `amount` option for the Stripe messaging element.
     * Subclasses must implement based on their data source.
     *
     * @returns {number}
     */
    getTotalCents() {
        throw new Error('Subclasses must implement getTotalCents()');
    }

    /**
     * Returns the three-letter currency code (e.g., 'USD', 'EUR').
     * Used as the `currency` option for the Stripe messaging element.
     * Subclasses must implement based on their data source.
     *
     * @returns {string}
     */
    getCurrency() {
        throw new Error('Subclasses must implement getCurrency()');
    }

    getCountryCode() {
        return this.countryCode;
    }

    getPaymentMethodTypes() {
        return this.paymentMethods.map(pm => pm.paymentMethodType);
    }

    /**
     * Builds the options object passed to elements.create() and element.update().
     *
     * @returns {Object} Stripe paymentMethodMessaging element options
     */
    buildMessageElementOptions() {
        this._options = this.actions.applyFilters(
            'get_message_element_options',
            {
                amount: this.getTotalCents(),
                currency: this.getCurrency(),
                paymentMethodTypes: this.getPaymentMethodTypes(),
                countryCode: this.getCountryCode()
            },
            this
        );
        return this._options;
    }
}

export default AbstractMessage;